import { Box } from '@factorialco/deprecated-design-system'
import { isSameDay } from 'date-fns'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { CycleSelector } from './_CycleSelector'
import { DateRangeSelector } from './_DateRangeSelector'
import { ALL_REGULAR_PERIODS, FixedDuration } from './constants'
import { Cycle, calculateAdjustedEndDateForCycle } from './cycleUtils'

const AVAILABLE_PERIODS_BY_CYCLE: Record<Cycle, FixedDuration[]> = {
  // Any period can be divided in days
  daily: ALL_REGULAR_PERIODS,
  // No period can be divided in weeks other than a week
  // (E.g. only February has 28 days, the rest aren't divisible by 7)
  weekly: ['week'],
  monthly: ['month', 'quarter', 'year'],
  quarterly: ['quarter', 'year'],
}

type Props = {
  /**
   * Require the user to choose a specific period from the ones available.
   *
   * Use ALL_REGULAR_PERIODS to allow everything.
   *
   * If not provided, allows the user to choose any range.
   *
   * Each option allows the user to select:
   * - Day: 1 day
   * - Week: 1 week
   * - Month: 1 month
   * - Quarter: 1 quarter
   * - Year: 1 year
   * - Year to date: From the start of the year to today
   * - Custom: Any range
   */
  regularPeriods?: FixedDuration[]

  cycle?: Cycle
  startDate: Date
  endDate: Date
  longMonth?: boolean
  withRelativeDates?: boolean

  onCycleChange?: (cycle: Cycle) => void
  onDateRangeChange: (startDate: Date, endDate: Date) => void

  onCycleSelectorToggle?: (open: boolean) => void
}

export const DateNavigator = ({
  regularPeriods,
  cycle,
  startDate,
  endDate,
  longMonth,
  withRelativeDates,
  onCycleChange,
  onDateRangeChange,
}: Props) => {
  // Depending on the cycle selected, some regular periods can't be chosen
  const filteredRegularPeriods = useUnsafeMemo(() => {
    if (!cycle || !regularPeriods) {
      return regularPeriods
    }

    const availablePeriods = AVAILABLE_PERIODS_BY_CYCLE[cycle]

    return regularPeriods.filter((period) => availablePeriods.includes(period))
  }, [cycle, regularPeriods])

  const handleCycleChange = useUnsafeCallback(
    (newCycle: Cycle) => {
      onCycleChange?.(newCycle)

      const adjustedEndDate = calculateAdjustedEndDateForCycle(startDate, endDate, newCycle)

      if (!isSameDay(endDate, adjustedEndDate)) {
        onDateRangeChange(startDate, adjustedEndDate)
      }
    },
    [startDate, endDate, onCycleChange, onDateRangeChange]
  )

  const handleDateRangeChange = useUnsafeCallback(
    (newStartDate: Date, newEndDate: Date) => {
      const adjustedEndDate = cycle
        ? calculateAdjustedEndDateForCycle(newStartDate, newEndDate, cycle)
        : newEndDate

      onDateRangeChange(newStartDate, adjustedEndDate)
    },
    [cycle, onDateRangeChange]
  )

  return (
    <Box flexDirection="row" gap="s8">
      {cycle && <CycleSelector cycle={cycle} onCycleChange={handleCycleChange} />}
      <DateRangeSelector
        regularPeriods={filteredRegularPeriods}
        cycle={cycle}
        startDate={startDate}
        endDate={endDate}
        longMonth={longMonth}
        withRelativeDates={withRelativeDates}
        onDateRangeChange={handleDateRangeChange}
      />
    </Box>
  )
}
